import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, Plus, Trash2, Terminal, Settings, Loader2 } from 'lucide-react';

/**
 * AdvancedRunModal - Modal for customizing launch with flags and env vars
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onLaunch: (options: { flags: string, envOverrides: object }) => void
 * - launcher: LauncherData
 * - profile: ProfileData
 * - machine: MachineData
 * - isLaunching: boolean
 */
const AdvancedRunModal = ({
  isOpen,
  onClose,
  onLaunch,
  launcher,
  profile,
  machine,
  isLaunching = false
}) => {
  // Command-line flags (single string)
  const [flags, setFlags] = useState('');
  
  // Environment overrides (array of {key, value} for UI management)
  const [envVars, setEnvVars] = useState([]);

  if (!isOpen) return null;

  // Add new environment variable row
  const handleAddEnvVar = () => {
    setEnvVars([...envVars, { key: '', value: '', id: Date.now() }]);
  };

  // Remove environment variable row
  const handleRemoveEnvVar = (id) => {
    setEnvVars(envVars.filter(v => v.id !== id));
  };

  // Update environment variable
  const handleEnvVarChange = (id, field, value) => {
    setEnvVars(envVars.map(v => 
      v.id === id ? { ...v, [field]: value } : v
    ));
  };

  // Build env overrides object from array
  const buildEnvOverrides = () => {
    const overrides = {};
    envVars.forEach(({ key, value }) => {
      if (key.trim()) {
        overrides[key.trim()] = value;
      }
    });
    return overrides;
  };

  // Handle launch
  const handleLaunch = () => {
    onLaunch({
      flags: flags.trim(),
      envOverrides: buildEnvOverrides()
    });
  };

  // Check if we have any customizations
  const hasCustomizations = flags.trim() || envVars.some(v => v.key.trim());

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Advanced Launch Options
            </h2>
            <p className="text-sm text-gray-400">
              Customize the launch with additional flags and environment variables
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Launch Context */}
        <div className="px-6 py-3 bg-gray-800/50 border-b border-gray-700">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-400">Launching:</span>
            <span className="font-medium text-white">{profile?.name || 'Unknown Profile'}</span>
            <span className="text-gray-500">on</span>
            <span className="font-medium text-blue-400">{machine?.hostname || 'Unknown Machine'}</span>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Command-Line Flags Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Terminal size={18} className="text-blue-400" />
              <h3 className="font-medium text-white">Command-Line Flags</h3>
            </div>
            <p className="text-sm text-gray-400 mb-3">
              Additional arguments appended to the launch command after profile defaults.
            </p>
            <input
              type="text"
              value={flags}
              onChange={(e) => setFlags(e.target.value)}
              placeholder='-verbose -log "C:\logs\app.log" --debug'
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none font-mono text-sm"
            />
            <p className="mt-2 text-xs text-gray-500">
              Example: -batch -silent -log "/path/to/log.txt"
            </p>
          </div>

          {/* Environment Variables Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Settings size={18} className="text-green-400" />
              <h3 className="font-medium text-white">Environment Overrides</h3>
            </div>
            <p className="text-sm text-gray-400 mb-3">
              Variables merged with profile defaults. Overrides take precedence.
            </p>

            {/* Env Vars Table */}
            {envVars.length > 0 && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden mb-3">
                {/* Header */}
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-4 py-2 bg-gray-800 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <span>Variable</span>
                  <span>Value</span>
                  <span className="w-10"></span>
                </div>
                
                {/* Rows */}
                {envVars.map((envVar, index) => (
                  <div 
                    key={envVar.id}
                    className={`grid grid-cols-[1fr_1fr_auto] gap-2 px-4 py-2 ${
                      index !== envVars.length - 1 ? 'border-b border-gray-700/50' : ''
                    }`}
                  >
                    <input
                      type="text"
                      value={envVar.key}
                      onChange={(e) => handleEnvVarChange(envVar.id, 'key', e.target.value)}
                      placeholder="VARIABLE_NAME"
                      className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:border-blue-500 outline-none font-mono text-sm"
                    />
                    <input
                      type="text"
                      value={envVar.value}
                      onChange={(e) => handleEnvVarChange(envVar.id, 'value', e.target.value)}
                      placeholder="value"
                      className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:border-blue-500 outline-none font-mono text-sm"
                    />
                    <button
                      onClick={() => handleRemoveEnvVar(envVar.id)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Variable Button */}
            <button
              onClick={handleAddEnvVar}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Plus size={16} />
              <span>Add Variable</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            {hasCustomizations ? (
              <span className="text-amber-400">
                ⚡ Custom options will be applied
              </span>
            ) : (
              <span>No custom options configured</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isLaunching}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLaunch}
              disabled={isLaunching}
              className={`
                flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors
                ${isLaunching
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500 text-white'
                }
              `}
            >
              {isLaunching ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Launching...</span>
                </>
              ) : (
                <>
                  <Play size={16} />
                  <span>Launch</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdvancedRunModal;

